import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Image, Nav, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { logoutUser, getCurrentUser } from "../../../redux/slices/authSlice";

import '../styles.css';

import { uploadProfilePicture, deleteProfilePicture } from "../../../redux/slices/userSlice";
import { getProfilePictureUrl, } from '../../../utils/imageUtils';
import UserInitialsAvatar from '../../../utils/userInitialAvatar';

import PersonalArea from '../PersonalArea/PersonalArea';
import ProfileArea from '../ProfileArea/ProfileArea';
import NotificationsArea from '../NotificationsArea/NotificationsArea';
import LanguageArea from '../LanguageArea/LanguageArea';
import SecurityArea from '../SecurityArea/SecurityArea';
import DocumentsTab from '../../organization/Tabs/DocumentsTab';

const Profile = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [activeArea, setActiveArea] = useState('personal');
    const currentUser = useSelector((state) => state.auth.user);
    const fileInputRef = useRef(null)

    useEffect(() => {
        if (!currentUser) {
            dispatch(getCurrentUser());
        }
    }, [dispatch, currentUser]);

    const handleLogout = () => {
        dispatch(logoutUser());
    };

    const handleImageClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            dispatch(uploadProfilePicture({ file }))
                .unwrap()
                .then(() => {
                    dispatch(getCurrentUser());
                })
                .catch((error) => {
                    console.log("Erro capturado na interface:", error);
                });
            e.target.value = null;
        }
    };

    const renderArea = () => {
        switch (activeArea) {
            case 'personal': return <PersonalArea />;
            case 'profile': return <ProfileArea />;
            case 'documents': return currentUser?.id ? <DocumentsTab userId={currentUser.id} /> : null;
            case 'notifications': return <NotificationsArea />;
            case 'language': return <LanguageArea />;
            case 'security': return <SecurityArea />;
            default: return <PersonalArea />;
        }
    };

    const tabs = [
        { id: 'personal', label: t('components.Profile.tabs.personal', 'Pessoal') },
        { id: 'profile', label: t('components.Profile.tabs.profile', 'Perfil') },
        { id: 'documents', label: t('components.Profile.tabs.documents', 'Documentos') },
        { id: 'notifications', label: t('components.Profile.tabs.notifications', 'Notificações') },
        { id: 'security', label: t('components.Profile.tabs.security', 'Segurança') },
        { id: 'language', label: t('components.Profile.tabs.language', 'Idioma') }
    ];

    return (
        <Container fluid className="py-4 px-md-5">
            <div className="atlar-card p-4 p-md-5 mx-auto w-100" style={{ maxWidth: '1200px' }}>

                <Row className="align-items-center mb-4">
                    <Col xs="auto">
                        <div
                            className="position-relative"
                            onClick={handleImageClick}
                            style={{ cursor: 'pointer', display: 'inline-block' }}
                            title={t('components.Profile.change_photo', 'Alterar foto de perfil')}
                        >
                            {currentUser?.profile_picture ? (
                                <Image
                                    src={getProfilePictureUrl(currentUser)}
                                    roundedCircle
                                    style={{ width: '100px', height: '100px', objectFit: 'cover', border: '2px solid var(--gray-300)' }}
                                />
                            ) : (
                                <UserInitialsAvatar 
                                    first_name={currentUser?.first_name || ''} 
                                    last_name={currentUser?.last_name || ''} 
                                    style={{ width: '100px', height: '100px', border: '2px solid var(--gray-300)' }} 
                                />
                            )}                            
                            <div
                                className="position-absolute w-100 h-100 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    top: 0, left: 0,
                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                    opacity: 0,
                                    transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                            >
                                <span className="text-white fw-bold" style={{ fontSize: '0.8rem' }}>{t('components.Profile.edit', 'Editar')}</span>
                            </div>
                        </div>
                        <Col xs="auto" className="d-flex flex-column align-items-center mt-0">
                            {currentUser?.profile_picture && (
                                <Button
                                    variant="link"
                                    className="txt-danger p-0 mt-2"
                                    style={{ fontSize: '0.8rem', textDecoration: 'none' }}
                                    onClick={() => dispatch(deleteProfilePicture()).unwrap().then(() => dispatch(getCurrentUser()))}
                                >
                                    {t('components.Profile.remove_photo', 'Remover Foto')}
                                </Button>
                            )}
                        </Col>

                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="image/jpeg, image/png"
                            onChange={handleFileChange}
                        />
                    </Col>
                    <Col>
                        <h2 className="txt-main fw-bold mb-1" style={{ fontSize: '1.5rem' }}>
                            {currentUser
                                ? `${currentUser.first_name || currentUser.username} ${currentUser.last_name || ''}`
                                : t('components.Profile.loading_user', 'A carregar...')
                            }
                            {currentUser?.job_title && (
                                <span className="txt-subtle fw-normal" style={{ fontSize: '1.2rem' }}>
                                    {' '} - {currentUser.job_title}
                                </span>
                            )}
                        </h2>
                        <p className="txt-subtle mb-0 fw-semibold" style={{ fontSize: '0.95rem' }}>
                            {currentUser?.location || t('components.Profile.location_not_defined', 'Localização não definida')} / {currentUser?.email || ''}
                        </p>
                    </Col>
                    <Col xs="auto" className="d-flex align-items-center mt-3 mt-md-0">
                        <Button className="btn btn-danger px-4 rounded-pill fw-bold" onClick={handleLogout}>
                            {t('components.Profile.logout', 'Logout')}
                        </Button>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col>
                        <div style={{ borderBottom: '2px solid var(--gray-200)' }}>
                            <Nav className="flex-row flex-nowrap overflow-auto custom-scrollbar">
                                {tabs.map((tab) => (
                                    <Nav.Item key={tab.id}>
                                        <Nav.Link
                                            className={`fw-bold px-4 py-3 ${activeArea === tab.id ? 'txt-brand' : 'txt-subtle'}`}
                                            onClick={() => setActiveArea(tab.id)}
                                            style={{
                                                backgroundColor: 'transparent',
                                                borderBottom: activeArea === tab.id ? '3px solid var(--brand-primary)' : '3px solid transparent',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                whiteSpace: 'nowrap',
                                                marginBottom: '-2px'
                                            }}
                                        >
                                            {tab.label}
                                        </Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className="pt-2">
                        {renderArea()}
                    </Col>
                </Row>
            </div>
        </Container>
    );
}

export default Profile;